import { checkPermissions, permissionsByEntity, useAuth, useCheckPermissions } from '@cmg/auth';
import type { ColGroupDef, DataGridContextType, GridState } from '@cmg/data-grid';
import { getColDefId } from '@cmg/data-grid';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import { useEffect, useMemo, useRef, useState } from 'react';

import { CalendarCategory } from '../../../types/domain/calendar/constants';
import { getGraphqlWhere } from '../../calendar/hooks/graphql-query';
import { useSellingRestrictionVisibility } from '../../calendar/model/column-visibility';
import { DEFAULT_FILTERS } from '../CalendarRoute.model';
import type { CalendarGridColDef } from '../components/calendar-grid/CalendarGrid.model';
import {
  allocationNotionalColDef,
  allocationSharesColDef,
  cikColDef,
  conditionalLockUpColDef,
  conversionPriceColDef,
  cornerstoneColDef,
  couponColDef,
  couponTalkHighColDef,
  couponTalkLowColDef,
  demandColDef,
  exchangeCountryColDef,
  exchangeMicColDef,
  exchangeRegionColDef,
  filingDateColDef,
  fillRateColDef,
  firstTradeDateColDef,
  grossProceedsTotalColDef,
  grossProceedsTotalUsdColDef,
  ioiNotesColDef,
  issuerColDef,
  leftLeadColDef,
  lockUpExpirationDateColDef,
  managersColDef,
  marketCapPostOfferingColDef,
  marketCapPostOfferingUsdColDef,
  multipleLockUpsColDef,
  numberOfBookrunnersColDef,
  numberOfNonBookrunnersColDef,
  offeringNotesColDef,
  offeringPriceColDef,
  offeringPriceUsdColDef,
  offeringStatusColDef,
  offeringTypeColDef,
  pctFileToOfferColDef,
  pctToCurrentColDef,
  pctToLastTradeColDef,
  pctToOneDayColDef,
  postponedDateColDef,
  premiumColDef,
  premiumTalkHighColDef,
  premiumTalkLowColDef,
  priceRangeColDef,
  priceRangeHighColDef,
  priceRangeLowColDef,
  pricingDateColDef,
  secondarySharesColDef,
  secRegisteredColDef,
  sectorColDef,
  securityTypeColDef,
  sellingRestrictionsColDef,
  sizeInSecuritiesTotalColDef,
  symbolColDef,
} from '../components/calendar-grid/columns';
import type { TabValue } from '../components/calendar-tabs/CalendarTabs.model';
import { getCalendarTabStorageKey } from '../components/calendar-tabs/CalendarTabs.model';
import type { Calendar_GridRowDataQueryVariables } from '../graphql/__generated__/Calendar_GridRowData';
import { useGetCalendarGridRowData } from './useGetCalendarGridRowData';

type ColumnPermissions = {
  hasIoiPermission: boolean;
  hasSellingRestrictionPermission: boolean;
};

type CalendarGridConfig = {
  initialGridState?: GridState;
  reverseNullSort?: boolean;
  getColumnDefs: (params: {
    permissions: ColumnPermissions;
    withGranularPriceRangeColumns?: boolean;
  }) => CalendarGridColDef[];
  getQueryVariables?: (args: {
    userId: string;
    userPermissions: string[];
  }) => Pick<Calendar_GridRowDataQueryVariables, 'where'>;
};

export const GRANULAR_PRICE_RANGE_COLUMNS = [
  couponTalkLowColDef,
  couponTalkHighColDef,
  premiumTalkLowColDef,
  premiumTalkHighColDef,
  priceRangeHighColDef,
  priceRangeLowColDef,
];

/**
 * Common column definitions used across different calendar grid types.
 */
export const COMMON_COL_DEFS: readonly CalendarGridColDef[] = [
  issuerColDef,
  symbolColDef,
  cikColDef,
  exchangeRegionColDef,
  exchangeCountryColDef,
  exchangeMicColDef,
  sellingRestrictionsColDef,
  secRegisteredColDef,
  sectorColDef,
  offeringTypeColDef,
  securityTypeColDef,
  sizeInSecuritiesTotalColDef,
  secondarySharesColDef,
  grossProceedsTotalColDef,
  grossProceedsTotalUsdColDef,
  leftLeadColDef,
  managersColDef,
  numberOfBookrunnersColDef,
  numberOfNonBookrunnersColDef,
  offeringNotesColDef,
  ioiNotesColDef,
] as const;

const COMMON_INITIAL_STATE: GridState = {
  columnVisibility: {
    hiddenColIds: [
      getColDefId(conversionPriceColDef),
      getColDefId(allocationSharesColDef),
      getColDefId(fillRateColDef),
      getColDefId(cikColDef),
      getColDefId(exchangeMicColDef),
      getColDefId(exchangeRegionColDef),
      getColDefId(secRegisteredColDef),
      getColDefId(securityTypeColDef),
      getColDefId(secondarySharesColDef),
      getColDefId(pctFileToOfferColDef),
      getColDefId(numberOfBookrunnersColDef),
      getColDefId(numberOfNonBookrunnersColDef),
      getColDefId(offeringNotesColDef),
      getColDefId(ioiNotesColDef),
    ],
  },
  columnPinning: {
    leftColIds: [getColDefId(issuerColDef)],
    rightColIds: [],
  },
};

const FILTER_LAYOUT_GROUPS: Record<string, readonly CalendarGridColDef[]> = {
  'Common Filters': [
    { colId: getColDefId(offeringTypeColDef) },
    { colId: getColDefId(grossProceedsTotalUsdColDef) },
    { colId: getColDefId(marketCapPostOfferingUsdColDef) },
    { colId: getColDefId(sectorColDef) },
    { colId: getColDefId(leftLeadColDef) },
    { colId: getColDefId(managersColDef) },
    {
      colId: getColDefId(exchangeCountryColDef),
      headerName: 'Region/Country',
    },
  ],
  Issuer: [
    { colId: getColDefId(cikColDef) },
    { colId: getColDefId(exchangeCountryColDef) },
    { colId: getColDefId(exchangeMicColDef) },
    { colId: getColDefId(issuerColDef) },
    { colId: getColDefId(offeringTypeColDef) },
    { colId: getColDefId(securityTypeColDef) },
    { colId: getColDefId(exchangeRegionColDef) },
    { colId: getColDefId(sectorColDef) },
    { colId: getColDefId(symbolColDef) },
    { colId: getColDefId(offeringNotesColDef) },
    { colId: getColDefId(ioiNotesColDef) },
  ],
  'My Participation': [
    { colId: getColDefId(allocationNotionalColDef) },
    { colId: getColDefId(allocationSharesColDef) },
    { colId: getColDefId(demandColDef) },
    { colId: getColDefId(fillRateColDef) },
  ],
  'Offering Terms': [
    { colId: getColDefId(secondarySharesColDef) },
    { colId: getColDefId(priceRangeColDef) },
    { colId: getColDefId(couponColDef) },
    { colId: getColDefId(grossProceedsTotalColDef) },
    { colId: getColDefId(grossProceedsTotalUsdColDef) },
    { colId: getColDefId(marketCapPostOfferingColDef) },
    { colId: getColDefId(marketCapPostOfferingUsdColDef) },
    { colId: getColDefId(offeringPriceUsdColDef) },
    { colId: getColDefId(offeringPriceColDef) },
    { colId: getColDefId(conversionPriceColDef) },
    { colId: getColDefId(premiumColDef) },
    { colId: getColDefId(sellingRestrictionsColDef) },
    { colId: getColDefId(secRegisteredColDef) },
    { colId: getColDefId(sizeInSecuritiesTotalColDef) },
  ],
  Ownership: [{ colId: getColDefId(cornerstoneColDef) }],
  Performance: [
    { colId: getColDefId(pctToOneDayColDef) },
    { colId: getColDefId(pctToCurrentColDef) },
    { colId: getColDefId(pctFileToOfferColDef) },
    { colId: getColDefId(pctToLastTradeColDef) },
  ],
  'Pricing Discount': [],
  Timing: [
    { colId: getColDefId(firstTradeDateColDef) },
    { colId: getColDefId(pricingDateColDef) },
    { colId: getColDefId(filingDateColDef) },
    { colId: getColDefId(postponedDateColDef) },
    { colId: getColDefId(lockUpExpirationDateColDef) },
    { colId: getColDefId(conditionalLockUpColDef) },
    { colId: getColDefId(multipleLockUpsColDef) },
  ],
  Underwriting: [
    { colId: getColDefId(numberOfBookrunnersColDef) },
    { colId: getColDefId(numberOfNonBookrunnersColDef) },
    { colId: getColDefId(leftLeadColDef) },
    { colId: getColDefId(managersColDef) },
  ],
};

const columnDefsFactory =
  (columnDefs = COMMON_COL_DEFS): CalendarGridConfig['getColumnDefs'] =>
  ({ permissions, withGranularPriceRangeColumns }) => {
    const { hasIoiPermission, hasSellingRestrictionPermission } = permissions;

    return columnDefs
      .filter(columnDef => {
        if (columnDef === sellingRestrictionsColDef) {
          return hasSellingRestrictionPermission;
        }

        if (
          columnDef === demandColDef ||
          columnDef === fillRateColDef ||
          columnDef === allocationSharesColDef ||
          columnDef === allocationNotionalColDef
        ) {
          return hasIoiPermission;
        }

        return true;
      })
      .filter(columnDef => {
        return withGranularPriceRangeColumns || !GRANULAR_PRICE_RANGE_COLUMNS.includes(columnDef);
      });
  };

const GRID_TYPE_CONFIG_MAP: Record<TabValue, CalendarGridConfig> = {
  live: {
    getColumnDefs: columnDefsFactory([
      ...COMMON_COL_DEFS,
      demandColDef,
      fillRateColDef,
      pricingDateColDef,
      marketCapPostOfferingColDef,
      marketCapPostOfferingUsdColDef,
      premiumTalkLowColDef,
      premiumTalkHighColDef,
      couponTalkLowColDef,
      couponTalkHighColDef,
      priceRangeLowColDef,
      priceRangeHighColDef,
      priceRangeColDef,
      cornerstoneColDef,
    ]),
    initialGridState: {
      ...COMMON_INITIAL_STATE,
      rowGroup: {
        groupColIds: [getColDefId(pricingDateColDef)],
      },
      sort: {
        sortModel: [
          { colId: getColDefId(pricingDateColDef), sort: 'asc' },
          { colId: getColDefId(issuerColDef), sort: 'asc' },
        ],
      },
      columnOrder: {
        orderedColIds: [
          getColDefId(issuerColDef),
          getColDefId(demandColDef),
          getColDefId(fillRateColDef),
          getColDefId(symbolColDef),
          getColDefId(cikColDef),
          getColDefId(exchangeRegionColDef),
          getColDefId(exchangeCountryColDef),
          getColDefId(exchangeMicColDef),
          getColDefId(sellingRestrictionsColDef),
          getColDefId(secRegisteredColDef),
          getColDefId(sectorColDef),
          getColDefId(offeringTypeColDef),
          getColDefId(securityTypeColDef),
          getColDefId(pricingDateColDef),
          getColDefId(grossProceedsTotalColDef),
          getColDefId(grossProceedsTotalUsdColDef),
          getColDefId(sizeInSecuritiesTotalColDef),
          getColDefId(secondarySharesColDef),
          getColDefId(marketCapPostOfferingColDef),
          getColDefId(marketCapPostOfferingUsdColDef),
          getColDefId(premiumTalkLowColDef),
          getColDefId(premiumTalkHighColDef),
          getColDefId(couponTalkLowColDef),
          getColDefId(couponTalkHighColDef),
          getColDefId(priceRangeLowColDef),
          getColDefId(priceRangeHighColDef),
          getColDefId(priceRangeColDef),
          getColDefId(leftLeadColDef),
          getColDefId(managersColDef),
          getColDefId(numberOfBookrunnersColDef),
          getColDefId(numberOfNonBookrunnersColDef),
          getColDefId(cornerstoneColDef),
          getColDefId(offeringNotesColDef),
          getColDefId(ioiNotesColDef),
        ],
      },
    },
    getQueryVariables: args => ({
      where: getGraphqlWhere({
        ...args,
        filters: DEFAULT_FILTERS,
        calendarCategory: CalendarCategory.LIVE,
      }),
    }),
  },
  priced: {
    getColumnDefs: columnDefsFactory([
      ...COMMON_COL_DEFS,
      allocationSharesColDef,
      allocationNotionalColDef,
      demandColDef,
      fillRateColDef,
      pricingDateColDef,
      firstTradeDateColDef,
      marketCapPostOfferingColDef,
      marketCapPostOfferingUsdColDef,
      premiumTalkLowColDef,
      premiumTalkHighColDef,
      couponTalkLowColDef,
      couponTalkHighColDef,
      priceRangeLowColDef,
      priceRangeHighColDef,
      priceRangeColDef,
      couponColDef,
      premiumColDef,
      conversionPriceColDef,
      offeringPriceColDef,
      offeringPriceUsdColDef,
      pctFileToOfferColDef,
      pctToLastTradeColDef,
      pctToOneDayColDef,
      pctToCurrentColDef,
      cornerstoneColDef,
    ]),
    reverseNullSort: true,
    initialGridState: {
      ...COMMON_INITIAL_STATE,
      rowGroup: {
        groupColIds: [getColDefId(firstTradeDateColDef)],
      },
      sort: {
        sortModel: [
          { colId: getColDefId(firstTradeDateColDef), sort: 'desc' },
          { colId: getColDefId(issuerColDef), sort: 'asc' },
        ],
      },
      columnOrder: {
        orderedColIds: [
          getColDefId(issuerColDef),
          getColDefId(allocationSharesColDef),
          getColDefId(allocationNotionalColDef),
          getColDefId(demandColDef),
          getColDefId(fillRateColDef),
          getColDefId(symbolColDef),
          getColDefId(cikColDef),
          getColDefId(exchangeRegionColDef),
          getColDefId(exchangeCountryColDef),
          getColDefId(exchangeMicColDef),
          getColDefId(sellingRestrictionsColDef),
          getColDefId(secRegisteredColDef),
          getColDefId(sectorColDef),
          getColDefId(offeringTypeColDef),
          getColDefId(securityTypeColDef),
          getColDefId(pricingDateColDef),
          getColDefId(firstTradeDateColDef),
          getColDefId(grossProceedsTotalColDef),
          getColDefId(grossProceedsTotalUsdColDef),
          getColDefId(sizeInSecuritiesTotalColDef),
          getColDefId(secondarySharesColDef),
          getColDefId(marketCapPostOfferingColDef),
          getColDefId(marketCapPostOfferingUsdColDef),
          getColDefId(premiumTalkLowColDef),
          getColDefId(premiumTalkHighColDef),
          getColDefId(couponTalkLowColDef),
          getColDefId(couponTalkHighColDef),
          getColDefId(priceRangeLowColDef),
          getColDefId(priceRangeHighColDef),
          getColDefId(priceRangeColDef),
          getColDefId(couponColDef),
          getColDefId(premiumColDef),
          getColDefId(conversionPriceColDef),
          getColDefId(offeringPriceColDef),
          getColDefId(offeringPriceUsdColDef),
          getColDefId(pctFileToOfferColDef),
          getColDefId(pctToLastTradeColDef),
          getColDefId(pctToOneDayColDef),
          getColDefId(pctToCurrentColDef),
          getColDefId(leftLeadColDef),
          getColDefId(managersColDef),
          getColDefId(numberOfBookrunnersColDef),
          getColDefId(numberOfNonBookrunnersColDef),
          getColDefId(cornerstoneColDef),
          getColDefId(offeringNotesColDef),
          getColDefId(ioiNotesColDef),
        ],
      },
    },
    getQueryVariables: args => ({
      where: getGraphqlWhere({
        ...args,
        filters: DEFAULT_FILTERS,
        calendarCategory: CalendarCategory.PRICED,
      }),
    }),
  },
  postponed: {
    getColumnDefs: columnDefsFactory([
      ...COMMON_COL_DEFS,
      demandColDef,
      fillRateColDef,
      postponedDateColDef,
      marketCapPostOfferingColDef,
      marketCapPostOfferingUsdColDef,
      priceRangeColDef,
    ]),
    reverseNullSort: true,
    initialGridState: {
      ...COMMON_INITIAL_STATE,
      rowGroup: {
        groupColIds: [getColDefId(postponedDateColDef)],
      },
      sort: {
        sortModel: [
          { colId: getColDefId(postponedDateColDef), sort: 'desc' },
          { colId: getColDefId(issuerColDef), sort: 'asc' },
        ],
      },
      columnOrder: {
        orderedColIds: [
          getColDefId(issuerColDef),
          getColDefId(demandColDef),
          getColDefId(fillRateColDef),
          getColDefId(symbolColDef),
          getColDefId(cikColDef),
          getColDefId(exchangeRegionColDef),
          getColDefId(exchangeCountryColDef),
          getColDefId(exchangeMicColDef),
          getColDefId(sellingRestrictionsColDef),
          getColDefId(secRegisteredColDef),
          getColDefId(sectorColDef),
          getColDefId(offeringTypeColDef),
          getColDefId(securityTypeColDef),
          getColDefId(postponedDateColDef),
          getColDefId(grossProceedsTotalColDef),
          getColDefId(grossProceedsTotalUsdColDef),
          getColDefId(sizeInSecuritiesTotalColDef),
          getColDefId(secondarySharesColDef),
          getColDefId(marketCapPostOfferingColDef),
          getColDefId(marketCapPostOfferingUsdColDef),
          getColDefId(priceRangeColDef),
          getColDefId(leftLeadColDef),
          getColDefId(managersColDef),
          getColDefId(numberOfBookrunnersColDef),
          getColDefId(numberOfNonBookrunnersColDef),
          getColDefId(offeringNotesColDef),
          getColDefId(ioiNotesColDef),
        ],
      },
    },
    getQueryVariables: args => ({
      where: getGraphqlWhere({
        ...args,
        filters: DEFAULT_FILTERS,
        calendarCategory: CalendarCategory.POSTPONED,
      }),
    }),
  },
  filed: {
    getColumnDefs: columnDefsFactory([
      ...COMMON_COL_DEFS,
      filingDateColDef,
      premiumTalkLowColDef,
      premiumTalkHighColDef,
      couponTalkLowColDef,
      couponTalkHighColDef,
      priceRangeLowColDef,
      priceRangeHighColDef,
      priceRangeColDef,
    ]),
    reverseNullSort: true,
    initialGridState: {
      ...COMMON_INITIAL_STATE,
      rowGroup: {
        groupColIds: [getColDefId(filingDateColDef)],
      },
      sort: {
        sortModel: [
          { colId: getColDefId(filingDateColDef), sort: 'desc' },
          { colId: getColDefId(issuerColDef), sort: 'asc' },
        ],
      },
      columnOrder: {
        orderedColIds: [
          getColDefId(issuerColDef),
          getColDefId(symbolColDef),
          getColDefId(cikColDef),
          getColDefId(exchangeRegionColDef),
          getColDefId(exchangeCountryColDef),
          getColDefId(exchangeMicColDef),
          getColDefId(sellingRestrictionsColDef),
          getColDefId(secRegisteredColDef),
          getColDefId(sectorColDef),
          getColDefId(offeringTypeColDef),
          getColDefId(securityTypeColDef),
          getColDefId(filingDateColDef),
          getColDefId(grossProceedsTotalColDef),
          getColDefId(grossProceedsTotalUsdColDef),
          getColDefId(sizeInSecuritiesTotalColDef),
          getColDefId(secondarySharesColDef),
          getColDefId(premiumTalkLowColDef),
          getColDefId(premiumTalkHighColDef),
          getColDefId(couponTalkLowColDef),
          getColDefId(couponTalkHighColDef),
          getColDefId(priceRangeLowColDef),
          getColDefId(priceRangeHighColDef),
          getColDefId(priceRangeColDef),
          getColDefId(leftLeadColDef),
          getColDefId(managersColDef),
          getColDefId(numberOfBookrunnersColDef),
          getColDefId(numberOfNonBookrunnersColDef),
          getColDefId(offeringNotesColDef),
          getColDefId(ioiNotesColDef),
        ],
      },
    },
    getQueryVariables: args => ({
      where: getGraphqlWhere({
        ...args,
        filters: DEFAULT_FILTERS,
        calendarCategory: CalendarCategory.FILED,
      }),
    }),
  },
  lockupExpirations: {
    getColumnDefs: columnDefsFactory([
      ...COMMON_COL_DEFS,
      allocationSharesColDef,
      allocationNotionalColDef,
      demandColDef,
      fillRateColDef,
      lockUpExpirationDateColDef,
      pricingDateColDef,
      conditionalLockUpColDef,
      multipleLockUpsColDef,
      marketCapPostOfferingColDef,
      marketCapPostOfferingUsdColDef,
      offeringPriceColDef,
      offeringPriceUsdColDef,
      pctToCurrentColDef,
    ]),
    initialGridState: {
      ...COMMON_INITIAL_STATE,
      rowGroup: {
        groupColIds: [getColDefId(lockUpExpirationDateColDef)],
      },
      sort: {
        sortModel: [
          { colId: getColDefId(lockUpExpirationDateColDef), sort: 'asc' },
          { colId: getColDefId(issuerColDef), sort: 'asc' },
        ],
      },
      columnOrder: {
        orderedColIds: [
          getColDefId(issuerColDef),
          getColDefId(allocationSharesColDef),
          getColDefId(allocationNotionalColDef),
          getColDefId(demandColDef),
          getColDefId(fillRateColDef),
          getColDefId(symbolColDef),
          getColDefId(cikColDef),
          getColDefId(exchangeRegionColDef),
          getColDefId(exchangeCountryColDef),
          getColDefId(exchangeMicColDef),
          getColDefId(sellingRestrictionsColDef),
          getColDefId(secRegisteredColDef),
          getColDefId(sectorColDef),
          getColDefId(offeringTypeColDef),
          getColDefId(securityTypeColDef),
          getColDefId(pricingDateColDef),
          getColDefId(lockUpExpirationDateColDef),
          getColDefId(conditionalLockUpColDef),
          getColDefId(multipleLockUpsColDef),
          getColDefId(grossProceedsTotalColDef),
          getColDefId(grossProceedsTotalUsdColDef),
          getColDefId(sizeInSecuritiesTotalColDef),
          getColDefId(secondarySharesColDef),
          getColDefId(marketCapPostOfferingColDef),
          getColDefId(marketCapPostOfferingUsdColDef),
          getColDefId(offeringPriceColDef),
          getColDefId(offeringPriceUsdColDef),
          getColDefId(pctToCurrentColDef),
          getColDefId(leftLeadColDef),
          getColDefId(managersColDef),
          getColDefId(numberOfBookrunnersColDef),
          getColDefId(numberOfNonBookrunnersColDef),
          getColDefId(offeringNotesColDef),
          getColDefId(ioiNotesColDef),
        ],
      },
    },
    getQueryVariables: args => ({
      where: getGraphqlWhere({
        ...args,
        filters: DEFAULT_FILTERS,
        calendarCategory: CalendarCategory.LOCK_UP_EXPIRATION,
      }),
    }),
  },
  myOfferings: {
    getColumnDefs: columnDefsFactory([
      ...COMMON_COL_DEFS,
      allocationSharesColDef,
      allocationNotionalColDef,
      demandColDef,
      fillRateColDef,
      pricingDateColDef,
      marketCapPostOfferingColDef,
      marketCapPostOfferingUsdColDef,
      offeringStatusColDef,
      premiumTalkLowColDef,
      premiumTalkHighColDef,
      couponTalkLowColDef,
      couponTalkHighColDef,
      priceRangeLowColDef,
      priceRangeHighColDef,
      priceRangeColDef,
      conversionPriceColDef,
      offeringPriceColDef,
      offeringPriceUsdColDef,
    ]),
    reverseNullSort: true,
    initialGridState: {
      ...COMMON_INITIAL_STATE,
      rowGroup: {
        groupColIds: [getColDefId(pricingDateColDef)],
      },
      sort: {
        sortModel: [
          { colId: getColDefId(pricingDateColDef), sort: 'desc' },
          { colId: getColDefId(issuerColDef), sort: 'asc' },
        ],
      },
      columnOrder: {
        orderedColIds: [
          getColDefId(issuerColDef),
          getColDefId(allocationSharesColDef),
          getColDefId(allocationNotionalColDef),
          getColDefId(demandColDef),
          getColDefId(fillRateColDef),
          getColDefId(symbolColDef),
          getColDefId(offeringStatusColDef),
          getColDefId(cikColDef),
          getColDefId(exchangeRegionColDef),
          getColDefId(exchangeCountryColDef),
          getColDefId(exchangeMicColDef),
          getColDefId(sellingRestrictionsColDef),
          getColDefId(secRegisteredColDef),
          getColDefId(sectorColDef),
          getColDefId(offeringTypeColDef),
          getColDefId(securityTypeColDef),
          getColDefId(pricingDateColDef),
          getColDefId(grossProceedsTotalColDef),
          getColDefId(grossProceedsTotalUsdColDef),
          getColDefId(sizeInSecuritiesTotalColDef),
          getColDefId(secondarySharesColDef),
          getColDefId(marketCapPostOfferingColDef),
          getColDefId(marketCapPostOfferingUsdColDef),
          getColDefId(premiumTalkLowColDef),
          getColDefId(premiumTalkHighColDef),
          getColDefId(couponTalkLowColDef),
          getColDefId(couponTalkHighColDef),
          getColDefId(priceRangeLowColDef),
          getColDefId(priceRangeHighColDef),
          getColDefId(priceRangeColDef),
          getColDefId(conversionPriceColDef),
          getColDefId(offeringPriceColDef),
          getColDefId(offeringPriceUsdColDef),
          getColDefId(leftLeadColDef),
          getColDefId(managersColDef),
          getColDefId(numberOfBookrunnersColDef),
          getColDefId(numberOfNonBookrunnersColDef),
          getColDefId(offeringNotesColDef),
          getColDefId(ioiNotesColDef),
        ],
      },
    },
    getQueryVariables: args => {
      const canAccessMyOfferingsWithAllocations = checkPermissions(args.userPermissions, [
        permissionsByEntity.Ioi.READ,
      ]);

      const calendarCategory = canAccessMyOfferingsWithAllocations
        ? CalendarCategory.MY_OFFERINGS_WITH_ALLOCATIONS
        : CalendarCategory.MY_OFFERINGS;

      return {
        where: getGraphqlWhere({ ...args, filters: DEFAULT_FILTERS, calendarCategory }),
      };
    },
  },
};

export type Props = {
  tab: TabValue;
  /**
   * Whether to include granular price range columns in the grid.
   * Used for Excel export use case as it should include extra columns for price range.
   *
   * @default false
   */
  withGranularPriceRangeColumns?: boolean;
};

export const useGetCalendarGridProps = ({ tab, withGranularPriceRangeColumns = false }: Props) => {
  const { oidcUserId, userPermissions } = useAuth();
  const { canReadConverts, canReadIntl } = useSellingRestrictionVisibility();
  const hasSellingRestrictionPermission = canReadConverts && canReadIntl;
  const hasIoiPermission = useCheckPermissions([permissionsByEntity.Ioi.READ]);

  const dataGridContextRef = useRef<DataGridContextType>();
  const [activeFilterCount, setActiveFilterCount] = useState<number>(0);

  const { getColumnDefs, getQueryVariables, initialGridState, reverseNullSort } =
    GRID_TYPE_CONFIG_MAP[tab];

  const [columnDefs, setColumnDefs] = useState<CalendarGridColDef[]>([]);

  const filterLayout = useMemo(() => {
    const colIds = columnDefs
      .map(colDef => colDef.colId ?? colDef.field)
      .filter((colId): colId is string => !isNil(colId));

    return Object.entries(FILTER_LAYOUT_GROUPS).reduce<ColGroupDef[]>(
      (acc, [groupName, columns]) => {
        const children = columns.filter(
          column => !isNil(column.colId) && colIds.includes(column.colId)
        );

        return isEmpty(children) ? acc : [...acc, { headerName: groupName, children }];
      },
      []
    );
  }, [columnDefs]);

  useEffect(() => {
    setColumnDefs(
      getColumnDefs({
        withGranularPriceRangeColumns,
        permissions: { hasIoiPermission, hasSellingRestrictionPermission },
      })
    );
  }, [
    getColumnDefs,
    hasIoiPermission,
    hasSellingRestrictionPermission,
    withGranularPriceRangeColumns,
  ]);

  const [fetchGridRowData, gridRowDataQueryResult] = useGetCalendarGridRowData({
    variables: getQueryVariables?.({ userId: oidcUserId!, userPermissions }),
  });

  return {
    fetchGridRowData,
    ...gridRowDataQueryResult,
    columnDefs,
    filterLayout,
    initialState: initialGridState,
    reverseNullSort: !!reverseNullSort,
    storageKey: getCalendarTabStorageKey(tab),
    activeFilterCount,
    onActiveFilterCountChange: setActiveFilterCount,
    dataGridContextRef,
  };
};
